import { AppProps } from '@/components/Layout/PageWrapper';
import { SpeedInsights } from '@vercel/speed-insights/next';
import 'styles/globals.css';
import 'styles/globals-extras.css';
import {
  SessionManagerProvider,
  useSessionManager,
} from '@/lib/SessionTracking/sessionContext';
import { initializeGlobalErrorTracking } from '@/lib/ErrorTracking/globalInstrumentation';
import { setSessionStateAuthState } from '@/lib/SessionTracking/sessionStateUploader';
import {
  SessionProvider as NextAuthProvider,
  useSession,
} from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { AxiomWebVitals } from 'next-axiom';
import { BrandProvider } from '@/lib/brand/BrandContext';
import type { BrandContext } from '@/lib/brand/types';

const runWhenIdle = (work: () => void): (() => void) => {
  const browserWindow = window as Window & {
    requestIdleCallback?: (
      callback: IdleRequestCallback,
      options?: IdleRequestOptions
    ) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

  if (typeof browserWindow.requestIdleCallback === 'function') {
    const idleHandle = browserWindow.requestIdleCallback(
      () => {
        work();
      },
      { timeout: 1200 }
    );
    return () => {
      browserWindow.cancelIdleCallback?.(idleHandle);
    };
  }

  const timeoutHandle = window.setTimeout(work, 400);
  return () => {
    window.clearTimeout(timeoutHandle);
  };
};

const SessionLifecycleInitializer = () => {
  const router = useRouter();
  const { data: session, status } = useSession();
  const sessionManager = useSessionManager();
  const teardownRef = useRef<(() => void) | null>(null);

  // Initialize session lifecycle when auth state is known
  useEffect(() => {
    if (!sessionManager) return;
    if (status === 'loading') return;

    void sessionManager.start(session?.user?.id ?? null);
  }, [sessionManager, status, session?.user?.id]);

  useEffect(() => {
    if (status === 'authenticated') {
      setSessionStateAuthState('authenticated');
      return;
    }
    if (status === 'unauthenticated') {
      setSessionStateAuthState('unauthenticated');
      return;
    }
    setSessionStateAuthState('unknown');
  }, [status]);

  // Install global instrumentation once per session manager
  useEffect(() => {
    if (!sessionManager) return;
    if (status === 'loading') return;

    const cancelIdle = runWhenIdle(() => {
      if (teardownRef.current) return;
      const teardown = initializeGlobalErrorTracking(sessionManager);
      teardownRef.current = typeof teardown === 'function' ? teardown : null;
    });

    return () => {
      cancelIdle();
      teardownRef.current?.();
      teardownRef.current = null;
    };
  }, [sessionManager, status]);

  // Track navigation
  useEffect(() => {
    if (!sessionManager) return;
    if (status === 'loading') return;

    const handleRouteChange = (url: string) => {
      sessionManager.pageView(url);
    };

    handleRouteChange(router.asPath);
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router, sessionManager, status]);

  return null;
};

function MyApp(props: AppProps) {
  const { Component, pageProps, session: legacySession } = props;
  const session =
    pageProps && 'session' in pageProps
      ? (pageProps.session as AppProps['session'] | null | undefined)
      : legacySession;
  const brand =
    pageProps && 'brand' in pageProps
      ? (pageProps.brand as BrandContext | null | undefined)
      : undefined;

  const contentElement =
    Component.PageWrapper !== undefined ? (
      <Component.PageWrapper {...props} />
    ) : (
      <Component {...pageProps} />
    );

  return (
    <>
      <NextAuthProvider
        session={session}
        basePath="/api/auth"
        refetchOnWindowFocus={false}
      >
        <BrandProvider brand={brand}>
          <SessionManagerProvider>
            <SessionLifecycleInitializer />
            {contentElement}
          </SessionManagerProvider>
        </BrandProvider>
      </NextAuthProvider>
      <SpeedInsights />
      <AxiomWebVitals />
    </>
  );
}

export default MyApp;
